import { z } from 'zod';

// Remote MCP server for ChatGPT (docs/mcp.md): OAuth 2.1 consent page + «Conexiones MCP». The OAuth protocol endpoints
// themselves (/.well-known/oauth-*, /api/v1/oauth/{register,authorize,token,revoke}) and the MCP transport
// (/api/v1/mcp) have wire formats fixed by RFC 8414/9728/7591/6749/7009 and the MCP spec, so they are not registry
// routes; only the JSON routes the web app calls are.

/** Web route of the consent page (the API's /oauth/authorize redirects the browser here with ?solicitud=<id>). */
export const OAUTH_CONSENT_PATH = '/oauth/authorize';
/** The only scope the authorization server issues: act on LIA through the MCP tools as the approving user. */
export const MCP_SCOPE = 'mcp';
export const MCP_SCOPE_LABELS: Record<string, string> = {
  [MCP_SCOPE]: 'Usar las herramientas de LIA (crear clientes y proyectos, cargar datos de onboarding, ajustar tono y prompts) en tu nombre',
};
/** Error codes the API puts in ?error= when it cannot even show the consent page (invalid client or redirect URI). */
export const OAUTH_CONSENT_ERRORS: Record<string, string> = {
  invalid_client: 'El cliente que intenta conectarse no está registrado en LIA. Vuelve a agregar el conector desde ChatGPT.',
  invalid_redirect_uri: 'La dirección de regreso del cliente no coincide con la que registró. Vuelve a agregar el conector desde ChatGPT.',
  invalid_request: 'La solicitud de autorización está incompleta o es inválida.',
  expired: 'La solicitud de autorización caducó. Vuelve a intentarlo desde ChatGPT.',
};

export const OauthAuthorizationRequestSchema = z.object({
  requestId: z.string(),
  client: z.object({
    name: z.string(),
    /** Host the browser returns to after the decision (shown so the user can spot an unexpected destination). */
    redirectHost: z.string(),
  }),
  scopes: z.array(z.object({ key: z.string(), label: z.string() })),
  user: z.object({ name: z.string(), email: z.string() }),
  /** False when the signed-in user lacks the platform_mcp capability (the page explains it; approve would 403). */
  canApprove: z.boolean(),
  expiresAt: z.string(),
});
export type OauthAuthorizationRequest = z.infer<typeof OauthAuthorizationRequestSchema>;

export const OauthDecisionResultSchema = z.object({ redirectUrl: z.string() });
export type OauthDecisionResult = z.infer<typeof OauthDecisionResultSchema>;

export const McpGrantSchema = z.object({
  id: z.string(),
  clientName: z.string(),
  redirectHost: z.string(),
  user: z.object({ id: z.string(), name: z.string(), email: z.string() }),
  /** The caller's own connection (platform admins also see everyone else's). */
  own: z.boolean(),
  scope: z.string(),
  createdAt: z.string(),
  lastUsedAt: z.string().nullable(),
});
export type McpGrant = z.infer<typeof McpGrantSchema>;
export const McpGrantListSchema = z.object({ items: z.array(McpGrantSchema), canManageAll: z.boolean() });
export type McpGrantList = z.infer<typeof McpGrantListSchema>;
